import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AdminStatsCards } from "@/components/admin/AdminStatsCards";
import { BankerManagement } from "@/components/admin/BankerManagement";
import { ProductManagement } from "@/components/admin/ProductManagement";
import { DisbursementAnalytics } from "@/components/admin/DisbursementAnalytics";
import { AttendanceTracking } from "@/components/admin/AttendanceTracking";
import {
  CalendarIcon,
  Users,
  Filter,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Briefcase,
  Package,
  Calendar as CalendarDay,
  Phone,
  MessageSquare,
  TrendingUp,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getAdminStats,
  DateRangeFilter,
  AdminStats,
  getCommitmentTargets,
} from "@/services/adminService";
import { getAllActiveUsers } from "@/services/userService";
import { ApprovedUser, Permission } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/useRoles";
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  format,
} from "date-fns";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

type PeriodType = "today" | "month" | "custom";

export default function BackendPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions(user?.roles || []);

  const [period, setPeriod] = useState<PeriodType>("today");
  const [customDateRange, setCustomDateRange] = useState<{
    from?: Date;
    to?: Date;
  }>({});
  const [teamMemberFilter, setTeamMemberFilter] = useState<string>("all");
  const [availableUsers, setAvailableUsers] = useState<ApprovedUser[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    totalLoginToday: 0,
    loginAmount: 0,
    totalDisbursementToday: 0,
    disbursedAmount: 0,
    totalVendorVisits: 0,
    totalLeadsStatusUpdated: 0,
    totalLeadsInPipeline: 0,
    totalNewLeads: 0,
    totalNewVendorsAdded: 0,
  });
  const [loading, setLoading] = useState(true);

  // Mobile-specific states
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("attendance");
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const loadUsers = async () => {
    try {
      const users = await getAllActiveUsers();
      setAvailableUsers(users);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  // Calculate date range based on period
  const getDateRange = (): DateRangeFilter => {
    const now = new Date();

    switch (period) {
      case "today":
        return {
          startDate: startOfDay(now),
          endDate: endOfDay(now),
        };
      case "month":
        return {
          startDate: startOfMonth(now),
          endDate: endOfMonth(now),
        };
      case "custom":
        return {
          startDate: customDateRange.from || startOfDay(now),
          endDate: customDateRange.to || endOfDay(now),
        };
      default:
        return {
          startDate: startOfDay(now),
          endDate: endOfDay(now),
        };
    }
  };

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Load stats when period or filter changes
  useEffect(() => {
    loadStats();
  }, [period, customDateRange, teamMemberFilter]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      const teamMemberPhone =
        teamMemberFilter === "all" ? undefined : teamMemberFilter;
      const adminStats = await getAdminStats(dateRange, teamMemberPhone, {
        userId: user?.id,
        userPhone: user?.phone,
        officeId: user?.officeId,
        role: user?.primaryRole,
        roles: user?.roles,
      });

      // Fetch commitment targets based on period and filter
      const commitmentPeriod = period === "today" ? "today" : "month";
      const commitmentTargets = await getCommitmentTargets(
        commitmentPeriod,
        teamMemberPhone,
        {
          userId: user?.id,
          userPhone: user?.phone,
          officeId: user?.officeId,
          role: user?.primaryRole,
          roles: user?.roles,
        },
      );

      // Merge stats with targets
      setStats({
        ...adminStats,
        ...commitmentTargets,
      });
    } catch (error) {
      toast.error("Failed to load statistics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    if (!navigator.onLine) {
      toast.error("Cannot refresh while offline");
      return;
    }

    // Haptic feedback
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    try {
      await Promise.all([loadStats(), loadUsers()]);
      toast.success("Data refreshed successfully");
    } catch (error) {
      toast.error("Failed to refresh data");
    }
  };

  // Get selected user details
  const selectedUser =
    teamMemberFilter !== "all"
      ? availableUsers.find((u) => u.phone === teamMemberFilter)
      : null;

  // Contact handlers for selected user
  const handleCall = () => {
    if (selectedUser?.phone) {
      window.open(`tel:${selectedUser.phone}`);
    }
  };

  const handleWhatsApp = () => {
    if (selectedUser?.phone) {
      const formatCurrency = (amount: number) => {
        if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
        if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
        return `₹${amount.toLocaleString("en-IN")}`;
      };

      const message = `Hi ${selectedUser.name},

Here's your performance summary:
• Login: ${stats.totalLoginToday} files${stats.loginTargetFiles ? ` (Target: ${stats.loginTargetFiles})` : ""}${stats.loginTargetAmount ? ` - Target: ${formatCurrency(stats.loginTargetAmount)}` : ""}
• Disbursement: ${stats.totalDisbursementToday} files${stats.disbursementTargetFiles ? ` (Target: ${stats.disbursementTargetFiles})` : ""}${stats.disbursementTargetAmount ? ` - Target: ${formatCurrency(stats.disbursementTargetAmount)}` : ""}
• New Leads: ${stats.totalNewLeads}
• Vendor Visits: ${stats.totalVendorVisits}

Keep up the great work! 🎯`;

      const waNum = selectedUser.phone.replace(/\D/g, "");
      const waFull =
        waNum.startsWith("91") && waNum.length > 10 ? waNum : "91" + waNum;
      window.open(
        `https://wa.me/${waFull}?text=${encodeURIComponent(message)}`,
      );
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Ctrl/Cmd + R to refresh
      if ((e.metaKey || e.ctrlKey) && e.key === "r") {
        e.preventDefault();
        handleManualRefresh();
      }

      // Number keys 1-4 for tab navigation on desktop
      if (window.innerWidth >= 768) {
        const tabMap: { [key: string]: string } = {
          "1": "attendance",
          "2": "bankers",
          "3": "products",
          "4": "disbursement",
        };
        if (tabMap[e.key]) {
          setActiveTab(tabMap[e.key]);
          if ("vibrate" in navigator) {
            navigator.vibrate(30);
          }
        }
      }

      // F for filters toggle (mobile)
      if (e.key === "f" && window.innerWidth < 768) {
        setFiltersExpanded((prev) => !prev);
      }

      // T for today, M for month
      if (e.key === "t") {
        setPeriod("today");
      } else if (e.key === "m") {
        setPeriod("month");
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Back online");
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error("No internet connection", {
        duration: Infinity,
        id: "offline-toast",
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Early return if user doesn't have access (shouldn't happen due to route guard)
  if (!hasPermission(Permission.ACCESS_BACKEND_TOOLS)) {
    return (
      <AppLayout>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">
            You don't have permission to access this page.
          </p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 pt-6 max-w-7xl mx-auto space-y-2 md:space-y-6 md:overflow-visible overflow-y-auto h-full">
        {/* Header - Like Leads page */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/tools")}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <PageHeader
              title="Backend Tools"
              subtitle="Backend operations and management"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Offline indicator */}
            {!isOnline && (
              <Badge variant="destructive" className="animate-pulse">
                Offline
              </Badge>
            )}
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={handleManualRefresh}
              disabled={loading}
              className="shrink-0"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Mobile Filter Section - Collapsible */}
        <div className="block md:hidden">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="sticky top-0 z-10 w-full mb-3 bg-background border-2 tap-highlight-green"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {teamMemberFilter !== "all" && (
              <Badge
                variant="secondary"
                className="ml-2 bg-green-500 text-white"
              >
                Active
              </Badge>
            )}
            {filtersExpanded ? (
              <ChevronUp className="h-4 w-4 ml-auto" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-auto" />
            )}
          </Button>

          <AnimatePresence>
            {filtersExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <Card className="mb-3">
                  <CardContent className="p-3 space-y-3">
                    {/* Period Selection */}
                    <div>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />
                        Period
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          size="sm"
                          variant={period === "today" ? "default" : "outline"}
                          onClick={() => setPeriod("today")}
                          className={cn(
                            "text-xs h-8 tap-highlight-green",
                            period === "today" &&
                              "bg-green-500 hover:bg-green-600",
                          )}
                        >
                          Today
                        </Button>
                        <Button
                          size="sm"
                          variant={period === "month" ? "default" : "outline"}
                          onClick={() => setPeriod("month")}
                          className={cn(
                            "text-xs h-8 tap-highlight-green",
                            period === "month" &&
                              "bg-green-500 hover:bg-green-600",
                          )}
                        >
                          Month
                        </Button>
                        <Button
                          size="sm"
                          variant={period === "custom" ? "default" : "outline"}
                          onClick={() => setPeriod("custom")}
                          className={cn(
                            "text-xs h-8 tap-highlight-green",
                            period === "custom" &&
                              "bg-green-500 hover:bg-green-600",
                          )}
                        >
                          Custom
                        </Button>
                      </div>
                    </div>

                    {/* Custom Date Range */}
                    {period === "custom" && (
                      <div className="space-y-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                "w-full justify-start text-left font-normal text-xs",
                                !customDateRange.from &&
                                  "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-3 w-3" />
                              {customDateRange.from
                                ? format(customDateRange.from, "PPP")
                                : "From date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={customDateRange.from}
                              onSelect={(date) =>
                                setCustomDateRange((prev) => ({
                                  ...prev,
                                  from: date,
                                }))
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>

                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                "w-full justify-start text-left font-normal text-xs",
                                !customDateRange.to && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-3 w-3" />
                              {customDateRange.to
                                ? format(customDateRange.to, "PPP")
                                : "To date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={customDateRange.to}
                              onSelect={(date) =>
                                setCustomDateRange((prev) => ({
                                  ...prev,
                                  to: date,
                                }))
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}

                    {/* Team Member Filter */}
                    <div>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        Team Filter
                      </p>
                      <Select
                        value={teamMemberFilter}
                        onValueChange={setTeamMemberFilter}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Team Members</SelectItem>
                          {availableUsers.map((user) => (
                            <SelectItem key={user.phone} value={user.phone}>
                              {user.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Active Filter Indicator */}
                    {teamMemberFilter !== "all" && (
                      <div className="flex items-center gap-2 text-xs bg-green-50 dark:bg-green-950 p-2 rounded-md">
                        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-green-700 dark:text-green-300">
                          Filtering by:{" "}
                          {
                            availableUsers.find(
                              (u) => u.phone === teamMemberFilter,
                            )?.name
                          }
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop Filter Section - Original */}
        <Card className="hidden md:block">
          <CardContent className="p-4 md:p-6">
            <div className="space-y-6">
              {/* Date Range Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-medium text-sm">Date Range</h3>
                </div>
                <Tabs
                  value={period}
                  onValueChange={(v) => setPeriod(v as PeriodType)}
                >
                  <TabsList className="grid w-full grid-cols-3 max-w-md">
                    <TabsTrigger
                      value="today"
                      className="data-[state=active]:bg-green-500 data-[state=active]:text-white"
                    >
                      Today
                    </TabsTrigger>
                    <TabsTrigger
                      value="month"
                      className="data-[state=active]:bg-green-500 data-[state=active]:text-white"
                    >
                      This Month
                    </TabsTrigger>
                    <TabsTrigger
                      value="custom"
                      className="data-[state=active]:bg-green-500 data-[state=active]:text-white"
                    >
                      Custom Range
                    </TabsTrigger>
                  </TabsList>

                  {/* Custom Date Range Picker */}
                  {period === "custom" && (
                    <div className="mt-4 flex gap-2 items-center flex-wrap">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal",
                              !customDateRange.from && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {customDateRange.from ? (
                              format(customDateRange.from, "PPP")
                            ) : (
                              <span>From date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={customDateRange.from}
                            onSelect={(date) =>
                              setCustomDateRange((prev) => ({
                                ...prev,
                                from: date,
                              }))
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <span className="text-muted-foreground">to</span>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal",
                              !customDateRange.to && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {customDateRange.to ? (
                              format(customDateRange.to, "PPP")
                            ) : (
                              <span>To date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={customDateRange.to}
                            onSelect={(date) =>
                              setCustomDateRange((prev) => ({
                                ...prev,
                                to: date,
                              }))
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </Tabs>
              </div>

              <div className="border-t" />

              {/* Team Member Filter Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-medium text-sm">Team Member Filter</h3>
                </div>
                <Select
                  value={teamMemberFilter}
                  onValueChange={setTeamMemberFilter}
                >
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team Members</SelectItem>
                    {availableUsers.map((user) => (
                      <SelectItem key={user.phone} value={user.phone}>
                        {user.name} ({user.phone})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Visual Indicator */}
              {teamMemberFilter !== "all" && (
                <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="h-2 w-2 rounded-full bg-green-500 mt-1.5 animate-pulse" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-green-900 dark:text-green-100">
                      Filter Active
                    </p>
                    <p className="text-green-700 dark:text-green-300 text-xs mt-0.5">
                      Showing data for:{" "}
                      {availableUsers.find((u) => u.phone === teamMemberFilter)
                        ?.name || "Selected member"}
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-xs mt-1">
                      📊 Affects: Statistics, Disbursements
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-white dark:bg-gray-900"
                  >
                    Filtered
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status and Stats Section */}
        <div className="space-y-1">
          <h3 className="font-semibold text-green-800 text-base">
            {selectedUser ? `${selectedUser.name} Status` : "All User Status"}
          </h3>
          <AdminStatsCards stats={stats} loading={loading} />
        </div>

        {/* Contact Buttons for Selected User */}
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex gap-3"
          >
            <Button
              onClick={handleCall}
              className="flex-1 gap-2 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium"
            >
              <Phone className="h-4 w-4" />
              Call for Status
            </Button>
            <Button
              onClick={handleWhatsApp}
              className="flex-1 gap-2 h-10 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium"
            >
              <MessageSquare className="h-4 w-4" />
              WhatsApp Status
            </Button>
          </motion.div>
        )}

        {/* Backend Tools Wrapper Card - Mobile View */}
        <div className="block md:hidden">
          <Card className="border shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                Backend Tools
              </p>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { id: "attendance", icon: CalendarDay, label: "Attendance" },
                  { id: "bankers", icon: Users, label: "Bankers" },
                  { id: "products", icon: Package, label: "Products" },
                  {
                    id: "disbursement",
                    icon: TrendingUp,
                    label: "Disbursement",
                  },
                ].map((tab) => (
                  <motion.button
                    key={tab.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.05 }}
                    onClick={() => {
                      if ("vibrate" in navigator) navigator.vibrate(30);
                      setActiveTab(tab.id);
                    }}
                    className={cn(
                      "flex flex-col items-center justify-center p-2 rounded-xl transition-all tap-highlight-green",
                      activeTab === tab.id
                        ? "bg-green-500 text-white shadow-md ring-2 ring-green-300"
                        : "bg-muted/40 text-muted-foreground active:bg-muted",
                    )}
                  >
                    <div
                      className={cn(
                        "p-1.5 rounded-lg mb-1",
                        activeTab === tab.id ? "bg-white/20" : "bg-muted",
                      )}
                    >
                      <tab.icon className="h-4 w-4" />
                    </div>
                    <span className="text-[10px] font-medium">{tab.label}</span>
                  </motion.button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile: Tab Content with Animation */}
        <div className="block md:hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === "bankers" && (
                <BankerManagement hideAdd={false} hideDelete={true} />
              )}
              {activeTab === "products" && (
                <ProductManagement hideDelete={true} />
              )}
              {activeTab === "disbursement" && (
                <DisbursementAnalytics
                  teamMemberFilter={teamMemberFilter}
                  currentMonthOnly={true}
                />
              )}
              {activeTab === "attendance" && <AttendanceTracking />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop: Original Tabs Layout */}
        <Tabs defaultValue="attendance" className="hidden md:block space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger
              value="attendance"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Attendance
            </TabsTrigger>
            <TabsTrigger
              value="bankers"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Bankers
            </TabsTrigger>
            <TabsTrigger
              value="products"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Products
            </TabsTrigger>
            <TabsTrigger
              value="disbursement"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Disbursement
            </TabsTrigger>
          </TabsList>

          <TabsContent value="attendance">
            <AttendanceTracking />
          </TabsContent>

          <TabsContent value="bankers">
            <BankerManagement hideAdd={false} hideDelete={true} />
          </TabsContent>

          <TabsContent value="products">
            <ProductManagement hideDelete={true} />
          </TabsContent>

          <TabsContent value="disbursement">
            <DisbursementAnalytics
              teamMemberFilter={teamMemberFilter}
              currentMonthOnly={true}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
